import React, { useState, useEffect } from "react";
import { apiFamilyGet, apiFamilyInvite, apiFamilyRemove, formatApiError } from "../lib/api";
import { Users, UserPlus, Loader2, Trash2, Crown, Clock } from "lucide-react";

export const FamilyManager = ({ t }) => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    apiFamilyGet().then(setData).catch(() => setData({ is_family: false }));
  }, []);

  if (!data) {
    return (
      <div className="mt-4 flex justify-center rounded-2xl border border-slate-200 bg-white py-8" data-testid="account-family-loading">
        <Loader2 className="h-5 w-5 animate-spin text-blue-700" />
      </div>
    );
  }
  if (!data.is_family) return null;

  const F = t("account.family");
  const usage = data.usage || {};
  const pct = usage.limit ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 0;
  const ownerEmail = (m) => (m.role === "owner");

  const invite = async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!val) return;
    setBusy("invite"); setErr("");
    try { const d = await apiFamilyInvite(val); setData(d); setEmail(""); }
    catch (e2) { setErr(formatApiError(e2?.response?.data?.detail, F.errorGeneric)); }
    finally { setBusy(""); }
  };

  const remove = async (em) => {
    setBusy(`rm-${em}`); setErr("");
    try { const d = await apiFamilyRemove(em); setData(d); }
    catch (e2) { setErr(formatApiError(e2?.response?.data?.detail, F.errorGeneric)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-6" data-testid="account-family">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-blue-700" />
        <h2 className="font-semibold text-slate-900">{F.title}</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        {data.is_member ? F.memberDesc.replace("{owner}", data.owner_email || "") : F.desc}
      </p>

      {/* Shared usage */}
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="account-family-usage">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700">{F.sharedUsage}</span>
          <span className="text-slate-500">{usage.used ?? 0} / {usage.limit ?? 0}</span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-[#0D9488] transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Members list */}
      <div className="mt-4 space-y-2" data-testid="account-family-members">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-600">{F.membersTitle}</h3>
          <span className="text-xs text-slate-500">{data.used_profiles} / {data.max_profiles} {F.profiles}</span>
        </div>
        {data.members.map((m) => (
          <div key={m.email} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`family-member-${m.email}`}>
            <div className="flex min-w-0 items-center gap-2">
              {m.role === "owner" && <Crown className="h-4 w-4 shrink-0 text-blue-700" />}
              <span className="truncate text-sm text-slate-700">{m.email}</span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${m.role === "owner" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>
                {m.role === "owner" ? F.ownerBadge : F.memberBadge}
              </span>
              {data.is_owner && m.role !== "owner" && (
                <button data-testid={`family-remove-${m.email}`} onClick={() => remove(m.email)} disabled={busy === `rm-${m.email}`}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-60">
                  {busy === `rm-${m.email}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>
        ))}
        {data.invites.map((inv) => (
          <div key={inv.email} className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 bg-white p-3" data-testid={`family-invite-${inv.email}`}>
            <div className="flex min-w-0 items-center gap-2">
              <Clock className="h-4 w-4 shrink-0 text-amber-500" />
              <span className="truncate text-sm text-slate-600">{inv.email}</span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">{F.pending}</span>
              {data.is_owner && (
                <button data-testid={`family-remove-${inv.email}`} onClick={() => remove(inv.email)} disabled={busy === `rm-${inv.email}`}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-60">
                  {busy === `rm-${inv.email}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Invite form (owner only) */}
      {data.is_owner && (
        <form onSubmit={invite} className="mt-4" data-testid="account-family-invite-form">
          {data.can_invite ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <input data-testid="family-invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={F.invitePlaceholder}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" />
              <button data-testid="family-invite-btn" type="submit" disabled={busy === "invite"}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60">
                {busy === "invite" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} {F.inviteBtn}
              </button>
            </div>
          ) : (
            <p className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-500" data-testid="account-family-full">{F.full}</p>
          )}
          {err && <p data-testid="account-family-error" className="mt-2 text-sm text-rose-500">{err}</p>}
        </form>
      )}
    </div>
  );
};
